import { useEffect, useState } from "react";
import { BookOpen, CircleCheck, Flag, PencilLine } from "lucide-react";
import Modal from "../../../components/Modal/Modal";
import Button from "../../../components/Button/Button";
import Loader from "../../../components/Loader/Loader";
import Table from "../../../components/Table/Table";
import Textarea from "../../../components/Textarea/Textarea";
import { diaryLessonTableConstants } from "../../WeeklyDiary/DiaryLessonTableConst";
import StatCard from "../../../components/StatCard/StatCard";
import StatusChip from "../../../components/StatusChip/StatusChip";
import { useWeeklyReview } from "../hooks/useWeeklyReview";
import ApprovalRemarks from "../../WeeklyDiary/components/ApprovalRemarks";
import { formatDate, getWeekRange } from "../utils/lessonPlanUtils";

/** Teacher's "Weekly Review & Diary": review the week's lessons (all subjects) and submit a reflection. */
const WeeklyReviewModal = ({ isOpen, weekStart, onClose, onSubmitted }) => {
  const { diary, isLoading, isSubmitting, fetchDiary, submitWeek } = useWeeklyReview();
  const [reflection, setReflection] = useState("");
  const [error, setError] = useState("");
  const { start, end } = getWeekRange(weekStart);
  const isLocked = diary.isSubmitted || diary.isApproved;

  useEffect(() => {
    if (isOpen) fetchDiary(weekStart);
  }, [isOpen, weekStart, fetchDiary]);

  useEffect(() => {
    setReflection(diary.reflection);
    setError("");
  }, [diary]);

  const handleSubmit = async () => {
    if (!reflection.trim()) {
      setError("Weekly reflection is required");
      return;
    }
    const isSaved = await submitWeek(weekStart, reflection, diary.subjectId);
    if (isSaved) {
      onSubmitted?.();
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={
        <span className="lp-modal-title">
          Weekly Review &amp; Diary
          <small>
            Week of {formatDate(start, "d")}-{formatDate(end, "d MMM''yy")}
          </small>
        </span>
      }
      footer={
        <div className="lp-modal-actions">
          <Button variant="secondary" onClick={onClose}>
            {isLocked ? "Close" : "Cancel"}
          </Button>
          {!isLocked && (
            <Button onClick={handleSubmit} loading={isSubmitting} disabled={isLoading}>
              Close Week &amp; Submit
            </Button>
          )}
        </div>
      }
    >
      <div className="d-flex flex-wrap align-items-center justify-content-end gap-3 mb-3">
        {diary.isApproved ? (
          <StatusChip tone="success">Approved</StatusChip>
        ) : (
          diary.isSubmitted && <StatusChip tone="warning">Submitted · awaiting approval</StatusChip>
        )}
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <div className="row">
            <StatCard compact value={diary.summary.total} label="Total lesson plans" icon={BookOpen} tone="primary" />
            <StatCard compact value={diary.summary.completed} label="Lessons Taught" icon={CircleCheck} tone="success" />
            <StatCard compact value={diary.summary.pending} label="Pending" icon={PencilLine} tone="warning" />
            <StatCard compact value={diary.summary.flagged} label="Flagged" icon={Flag} tone="danger" />
          </div>

          <div className="evaluating-table lp-review-table">
            <Table
              cols={diaryLessonTableConstants(diary.lessons)}
              data={diary.lessons}
              hoverable
              striped={false}
              getRowClassName={(lesson) => (lesson.isFlagged ? "lp-row-flagged" : "")}
            />
          </div>

          <div className="mt-3">
            <Textarea
              label="Weekly Reflection / Observations"
              name="reflection"
              id="lp-weekly-reflection"
              rows={4}
              value={reflection}
              readOnly={isLocked}
              required
              error={error}
              onChangeHandler={(event) => {
                setReflection(event.target.value);
                setError("");
              }}
              placeholder="Summarize this week's teaching - what went well, challenges faced, student observations, topics needing revisit and your plan for next week..."
            />
          </div>

          {diary.isApproved && (
            <div className="mt-3">
              <h6 className="fw-semibold mb-0">Principal&apos;s Remarks</h6>
              <ApprovalRemarks diary={diary} />
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default WeeklyReviewModal;
